/**
 * Tiled background of diverse kiwi characters for the auth screen.
 * Port of KiwiPatternBackground.swift — renders a grid of pixel-art kiwis at low opacity.
 * Uses CSS grid to fill the entire viewport regardless of screen size.
 */

import { KiwiSkinRenderer } from "@/components/brand/KiwiSkinRenderer";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";

const KIWI_CONFIGS: KiwiSkinConfig[] = [
  { name: "", variant: "knight", palette: "forest", environment: "none", artifact: "none", effect: "none", rarity: "Common" },
  { name: "", variant: "pirate", palette: "sunset", environment: "none", artifact: "golden_crown", effect: "none", rarity: "Rare" },
  { name: "", variant: "robot", palette: "neon", environment: "starfield", artifact: "none", effect: "none", rarity: "Epic" },
  { name: "", variant: "wizard", palette: "gold", environment: "none", artifact: "crystal_orb", effect: "none", rarity: "Legendary" },
  { name: "", variant: "ninja", palette: "ice", environment: "snowscape", artifact: "none", effect: "none", rarity: "Uncommon" },
  { name: "", variant: "angel", palette: "cherry", environment: "none", artifact: "none", effect: "rainbow_shimmer", rarity: "Rare" },
  { name: "", variant: "astronaut", palette: "ocean", environment: "none", artifact: "none", effect: "none", rarity: "Common" },
  { name: "", variant: "ghost", palette: "lavender", environment: "aurora", artifact: "none", effect: "none", rarity: "Epic" },
  { name: "", variant: "chef", palette: "mint", environment: "none", artifact: "none", effect: "sparkle_trail", rarity: "Uncommon" },
  { name: "", variant: "devil", palette: "ember", environment: "volcanic", artifact: "none", effect: "none", rarity: "Rare" },
  { name: "", variant: "nerd", palette: "bubblegum", environment: "none", artifact: "none", effect: "holographic", rarity: "Uncommon" },
  { name: "", variant: "dj", palette: "storm", environment: "none", artifact: "none", effect: "none", rarity: "Common" },
  { name: "", variant: "knight", palette: "retro", environment: "none", artifact: "none", effect: "none", rarity: "Common" },
  { name: "", variant: "ninja", palette: "night", environment: "starfield", artifact: "none", effect: "none", rarity: "Rare" },
  { name: "", variant: "robot", palette: "slate", environment: "none", artifact: "none", effect: "none", rarity: "Common" },
  { name: "", variant: "wizard", palette: "moss", environment: "none", artifact: "golden_crown", effect: "none", rarity: "Uncommon" },
];

const CELL_SIZE = 80;
const ROWS = 20;
const COLS = 16;

function scaleForCell(row: number, col: number): number {
  const hash = Math.abs((row * 31 + col * 17 + row * col * 7 + 13) % 100);
  return 0.55 + (hash / 100) * 0.45;
}

export function KiwiPatternBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden opacity-[0.07] dark:opacity-[0.04] pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${COLS}, ${CELL_SIZE}px)`,
          gridTemplateRows: `repeat(${ROWS}, ${CELL_SIZE}px)`,
          width: COLS * CELL_SIZE,
          height: ROWS * CELL_SIZE,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {Array.from({ length: ROWS * COLS }, (_, i) => {
          const row = Math.floor(i / COLS);
          const col = i % COLS;
          const index = (row * 7 + col) % KIWI_CONFIGS.length;
          const scale = scaleForCell(row, col);
          const size = CELL_SIZE * scale * 0.7;
          const isOddRow = row % 2 === 1;

          return (
            <div
              key={i}
              className="flex items-center justify-center"
              style={{
                marginLeft: isOddRow ? CELL_SIZE / 2 : 0,
                transform: isOddRow ? "scaleX(-1)" : undefined,
              }}
            >
              <div style={{ width: size, height: size }}>
                <KiwiSkinRenderer config={KIWI_CONFIGS[index]} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
